// 204 -- friendly full-page state for a detail/edit page whose initial load
// failed. Maps known API error codes (notably the 171 POPIA guardian-consent
// gate) to plain language instead of leaking raw error keys next to an empty
// form ("guardian_consent_required" was rendered verbatim to coaches).
import { useNavigate } from "react-router-dom";
import { Lock, ShieldAlert, AlertTriangle, ChevronLeft } from "lucide-react";
import { ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

function describe(error: unknown, entityLabel: string): { icon: typeof Lock; title: string; detail: string } {
  if (error instanceof ApiError) {
    if (error.message === "guardian_consent_required" || error.body?.error === "guardian_consent_required") {
      return {
        icon: ShieldAlert,
        title: "Waiting for guardian consent",
        detail:
          "This player's profile is locked until a parent or guardian approves the child-data consent. Once the family approves it on My Children, the profile opens up.",
      };
    }
    if (error.status === 403) {
      return {
        icon: Lock,
        title: "You don't have access",
        detail: `Your role doesn't have access to this ${entityLabel}.`,
      };
    }
    if (error.status === 404) {
      return {
        icon: AlertTriangle,
        title: "Not found",
        detail: `This ${entityLabel} doesn't exist or has been removed.`,
      };
    }
  }
  return {
    icon: AlertTriangle,
    title: `Couldn't load this ${entityLabel}`,
    detail: "Something went wrong. Please try again.",
  };
}

export function LoadErrorCard({ error, entityLabel }: { error: unknown; entityLabel: string }) {
  const navigate = useNavigate();
  const { icon: Icon, title, detail } = describe(error, entityLabel);
  return (
    <div
      className="mx-auto flex max-w-md flex-col items-center gap-3 rounded-2xl border border-border bg-card p-8 text-center shadow-sm"
      data-region="load-error"
    >
      <Icon className="h-8 w-8 text-muted-foreground" aria-hidden />
      <h2 className="text-lg font-semibold">{title}</h2>
      <p className="text-sm text-muted-foreground">{detail}</p>
      <Button variant="outline" size="sm" onClick={() => navigate(-1)} data-action="load-error-back">
        <ChevronLeft className="mr-1 h-4 w-4" /> Back
      </Button>
    </div>
  );
}
